/*
 * <lot-trace> — the lot register's resolver (sections/lab-lot-register.liquid). The section
 * server-renders one record per "Lotto" metaobject — only the fields that record holds (field,
 * cultivar, best-before, milling date and analysis when registered) — each keyed by its code
 * as written, and every record carries `hidden`.
 *
 * This element reads ?lot= on arrival, reveals the record whose code matches loosely and
 * prints the notice when none does. Nothing is parsed out of a code: it is the lot's name in
 * the register, matched to a record and nothing else. assets/lab-lot.js answers later lookups
 * in place with the same matching rule. It never injects record content.
 */
if (!customElements.get('lot-trace')) {
  customElements.define(
    'lot-trace',
    class LotTrace extends HTMLElement {
      connectedCallback() {
        this.records = Array.from(this.querySelectorAll('[data-lot-code]'));
        if (this.records.length === 0) return;

        this.select = this.querySelector('[data-lot-select]');
        this.notice = this.querySelector('[data-lot-notice]');
        this.param = this.getAttribute('data-param') || 'lot';
        // no default record: a bottle-holder wants their own lot, so nothing shows until asked
        const requested = this.readParam();
        const matched = requested ? this.find(requested) : null;
        const target = matched;

        this.showOnly(target);

        if (this.select && target) {
          this.select.value = target.dataset.lotCode;
          this.select.addEventListener('change', () => {
            const record = this.find(this.select.value);
            if (record) {
              this.showOnly(record);
              this.writeParam(record.dataset.lotCode);
              this.hideNotice();
            }
          });
        }

        if (requested && !matched) {
          this.showNotice(requested);
        } else {
          this.hideNotice();
        }

        this.classList.add('is-ready');

        // Theme editor: reveal the record a merchant selects.
        this.addEventListener('shopify:block:select', (event) => {
          const record = this.records.find((r) => r.dataset.blockId === event.detail.blockId);
          if (record) {
            this.showOnly(record);
            if (this.select) this.select.value = record.dataset.lotCode;
          }
        });
      }

      // Lot codes are handwritten or stamped on the bottle, so matching forgives what a
      // reader mistypes: case, spaces and punctuation, a missing leading L, O for 0.
      // Keep in step with norm() in assets/lab-lot.js.
      norm(value) {
        return (value == null ? '' : String(value)).toUpperCase().replace(/[^A-Z0-9]/g, '').replace(/O/g, '0').replace(/^L/, '');
      }

      readParam() {
        try {
          return this.norm(new URLSearchParams(window.location.search).get(this.param));
        } catch (error) {
          return '';
        }
      }

      writeParam(code) {
        try {
          const url = new URL(window.location.href);
          url.searchParams.set(this.param, code);
          window.history.replaceState({}, '', url);
        } catch (error) {
          /* history unavailable (e.g. sandboxed) — the record still switches */
        }
      }

      find(code) {
        const wanted = this.norm(code);
        if (!wanted) return null;
        return this.records.find((record) => this.norm(record.dataset.lotCode) === wanted) || null;
      }

      showOnly(target) {
        this.records.forEach((record) => {
          record.hidden = record !== target;
        });
      }

      showNotice(code) {
        if (!this.notice) return;
        // Unhide first so the role="status" live region is in the a11y tree when
        // its text changes, so the message is announced.
        this.notice.hidden = false;
        const template = this.notice.getAttribute('data-template');
        if (template) this.notice.textContent = template.replace('[lot]', code);
      }

      hideNotice() {
        if (this.notice) this.notice.hidden = true;
      }
    }
  );
}
